import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  ModalFooter,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  AspectRatio,
  Center,
  Divider,
  Grid,
  GridItem,
  IconButton,
  Tooltip,
  useColorModeValue,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiPlay,
  FiPause,
  FiDollarSign,
  FiCreditCard,
  FiSettings,
  FiVideo,
  FiEdit,
  FiCopy,
  FiPower,
  FiTrash2,
  FiRefreshCw,
  FiWifi,
  FiWifiOff,
  FiClock,
  FiActivity,
} from 'react-icons/fi';
import { CordeCommonModelsDtoMachineMachine, CordeCommonModelsDtoMachineGameSession } from '@/api/generated/v1/api';
import { useMachineService } from '../../hooks/useMachineService';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/contexts/userprofile/UserProfileContext';
import { GameInterface, GameTouchEvent } from '@/components/machine/GameInterface';
import { GameModal } from '@/components/machine/GameModal';
import { EditMachineModal } from '@/components/machine/EditMachineModal';
import { useBalanceService } from '@/hooks/useBalanceService';
import { useVideoStream } from '@/hooks/useVideoStream';
import { useInputEventsStream } from '@/hooks/useInputEventsStream';
import { TouchAction, ButtonAction } from '@/types/websocket';
import { getApiBaseUrl } from '@/config/constants';

const getWsBaseUrl = () => {
  const apiBaseUrl = getApiBaseUrl();
  // Convert HTTP URL to WebSocket URL
  return apiBaseUrl.replace(/^http/, 'ws');
};

const WS_BASE = import.meta.env.VITE_WS_URL || getWsBaseUrl();

interface MachineStatusUpdate {
  machine_id: string;
  old_status: string;
  new_status: string;
  timestamp: string;
  platform_id: string;
  category: string;
  name: string;
}

interface StatusHistoryItem {
  status: string;
  timestamp: Date;
  duration?: string;
}

export const MachinePage: React.FC = () => {
  const { machineId } = useParams<{ machineId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const { profile } = useUserProfile();
  const toast = useToast();
  const machineService = useMachineService();
  const balanceService = useBalanceService();

  const [machine, setMachine] = useState<CordeCommonModelsDtoMachineMachine | null>(null);
  const [session, setSession] = useState<CordeCommonModelsDtoMachineGameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [statusHistory, setStatusHistory] = useState<StatusHistoryItem[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const { isOpen: isTopUpOpen, onOpen: onTopUpOpen, onClose: onTopUpClose } = useDisclosure();
  const { isOpen: isCashOutOpen, onOpen: onCashOutOpen, onClose: onCashOutClose } = useDisclosure();
  const { isOpen: isGameOpen, onOpen: onGameOpen, onClose: onGameClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const [topUpAmount, setTopUpAmount] = useState<number>(10);
  const [cashOutAmount, setCashOutAmount] = useState<number>(0);
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
  const [currency, setCurrency] = useState<string>('');
  const [isTouching, setIsTouching] = useState(false);

  // Unified input events stream (buttons + touch) via hook
  const inputStream = useInputEventsStream({
    machineId: machineId || '',
    sessionId: session?.id || '',
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 3000,
  });

  // WebSocket connection ref (for status stream only)
  const wsRef = useRef<WebSocket | null>(null);
  const wsUrl = `${WS_BASE}/ws/v1/machine/status-stream`;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Get machine stream URL from database or default
    const getStreamUrl = (path: string) => {
        if (machine?.stream_url) {
            return `${machine.stream_url}${path}`;
        }
        // Default to ghost-wheel.ru with WSS
        return `wss://ghost-wheel.ru:50140${path}`;
    };

    const isSecureStream = useMemo(() => {
        const url = machine?.stream_url || 'wss://ghost-wheel.ru:50101';
        return url.toLowerCase().startsWith('wss://');
    }, [machine?.stream_url]);

  // Memoize stream URL to avoid unnecessary re-renders
    const streamUrl = useMemo(() => getStreamUrl('/stream/main'), [machine?.stream_url]);

  // Video streaming hook
    const videoStream = useVideoStream({
        wsUrl: streamUrl,
        videoElementId: 'mainVideoStream',
        autoReconnect: true,
        maxReconnectAttempts: 5,
        reconnectDelay: 3000,
        tlsConfig: isSecureStream ? {
            enabled: true,
            // In browser, client certificates are handled by browser/system
            // The server must be configured for mTLS authentication
        } : undefined,
    });

  const loadMachine = async () => {
    if (!machineId) return;

    try {
      setLoading(true);
      const data = await machineService.getMachine(machineId);
      setMachine(data);
      
      // Initialize status history
      if (data.status) {
        setStatusHistory([{
          status: data.status,
          timestamp: new Date(),
        }]);
      }
    } catch (error) {
      console.error('Failed to load machine:', error);
      toast({
        title: 'Error',
        description: 'Failed to load machine details',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMachine();
    // load user balances for currency list
    (async () => {
      try {
        const balances = await balanceService.getBalances();
        balancesRef.current = balances; // Update the ref with fetched balances
        const cur = balances.map((b: any) => b.currency).filter(Boolean);
        setAvailableCurrencies(cur);
        if (cur.length && !currency) setCurrency(cur[0]);
      } catch (e) {
        console.warn('Failed to load balances for currency list', e);
      }
    })();
  }, [machineId]);

  // WebSocket connection management
  const connectWebSocket = () => {
    if (!machineId) return;

    try {
      // Establish real WebSocket connection
      const wsConnectUrl = auth.accessToken 
        ? `${wsUrl}?access_token=${encodeURIComponent(auth.accessToken)}`
        : wsUrl;
      wsRef.current = new WebSocket(wsConnectUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setLastUpdate(new Date());
        toast({
          title: 'Connected',
          description: 'Real-time updates enabled',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data: any = JSON.parse(event.data);
          if (data.type === 'status_update') {
            handleStatusUpdate(data.payload as MachineStatusUpdate);
          }
        } catch (e) {
          console.error('Invalid WS message', e);
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000 * reconnectAttempts.current);
        }
      };

      wsRef.current.onerror = (err) => {
        console.error('WebSocket error', err);
        wsRef.current?.close();
      };
      
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setIsConnected(false);
      
      // Retry connection
      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000 * reconnectAttempts.current);
      }
    }
  };

  // Simulate status updates for demo
  const simulateStatusUpdates = () => {
    const statuses = ['FREE', 'BUSY', 'OFFLINE', 'DISABLED'];
    let currentIndex = 0;
    
    const interval = setInterval(() => {
      if (!isConnected) {
        clearInterval(interval);
        return;
      }
      
      const newStatus = statuses[currentIndex % statuses.length];
      currentIndex++;
      
             handleStatusUpdate({
         machine_id: machineId!,
         old_status: machine?.status || 'UNKNOWN',
         new_status: newStatus,
         timestamp: new Date().toISOString(),
         platform_id: 'default',
         category: machine?.category || '',
         name: machine?.name || '',
       });
    }, 10000); // Update every 10 seconds for demo

    return () => clearInterval(interval);
  };

  // Handle incoming status updates
  const handleStatusUpdate = (update: MachineStatusUpdate) => {
    if (update.machine_id !== machineId) return;

    setMachine(prev => prev ? { ...prev, status: update.new_status } : null);
    setLastUpdate(new Date());
    
    // Add to status history
    setStatusHistory(prev => {
      const newHistory = [...prev];
      const lastItem = newHistory[newHistory.length - 1];
      
      // Calculate duration for previous status
      if (lastItem && !lastItem.duration) {
        const duration = new Date().getTime() - lastItem.timestamp.getTime();
        lastItem.duration = formatDuration(duration);
      }
      
      // Add new status
      newHistory.push({
        status: update.new_status,
        timestamp: new Date(),
      });
      
      // Keep only last 10 items
      return newHistory.slice(-10);
    });

    toast({
      title: 'Status Updated',
      description: `Machine status changed to ${update.new_status}`,
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  // Disconnect WebSocket (status stream only; input stream managed by hook)
  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    setIsConnected(false);
    reconnectAttempts.current = 0;
  };

  const handleStartSession = async () => {
    console.log('Starting session for machine:', machine?.id, 'profile:', profile?.id);
    
    if (!machine?.id) {
      toast({
        title: 'Error',
        description: 'Machine ID not found',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!profile?.id) {
      toast({
        title: 'Error',
        description: 'User profile not loaded. Please ensure you have a default profile.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSessionLoading(true);
      console.log('Calling reserveMachine with:', machine.id, profile.id);
      
      const sessionData = await machineService.reserveMachine(machine.id, profile.id);
      console.log('Session created successfully:', sessionData);
      
      setSession(sessionData);
      // WebSocket will be connected by useEffect when session changes
      toast({
        title: 'Session Started',
        description: `Game session started successfully. Session ID: ${sessionData.id?.slice(-8)}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to start session:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start game session',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSessionLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!session?.id) return;

    try {
      setSessionLoading(true);
      await machineService.releaseMachine(session.id);
      setSession(null);
      // Input stream will disconnect automatically when sessionId becomes empty
      toast({
        title: 'Сессия завершена',
        description: 'Игровая сессия успешно завершена',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to end session:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось завершить игровую сессию',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSessionLoading(false);
    }
  };

  const handleTopUp = async () => {
    if (!session?.id) return;

    try {
      const amount = {
        units: Math.floor(topUpAmount),
        nanos: Math.floor((topUpAmount % 1) * 1000000000),
        currency: currency,
      };

      await machineService.topUpMachine({
        session_id: session.id,
        amount,
        currency,
      });

      toast({
        title: 'Пополнение выполнено',
        description: `Добавлено ${topUpAmount} ${currency} на машину`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onTopUpClose();
      setTopUpAmount(10);
    } catch (error) {
      console.error('Failed to top up:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось пополнить машину',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCashOut = async () => {
    if (!session?.id || cashOutAmount <= 0) return;

    try {
      const amount = {
        units: Math.floor(cashOutAmount),
        nanos: Math.floor((cashOutAmount % 1) * 1000000000),
        currency: currency,
      };

      await machineService.cashOutMachine({
        session_id: session.id,
        amount,
        currency,
      });

      toast({
        title: 'Вывод выполнен',
        description: `Выведено ${cashOutAmount} ${currency} с машины`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onCashOutClose();
      setCashOutAmount(0);
    } catch (error) {
      console.error('Failed to cash out:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось вывести средства с машины',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEnableMachine = async () => {
    if (!machineId) return;
    try {
      await machineService.enableMachine(machineId);
      toast({
        title: 'Success',
        description: 'Machine enabled successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await loadMachine();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to enable machine',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDisableMachine = async () => {
    if (!machineId) return;
    try {
      const result = await machineService.disableMachine(machineId);
      setMachine(result);
      toast({
        title: 'Success',
        description: 'Machine disabled successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disable machine',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCloneMachine = async () => {
    if (!machine?.id || !machine?.name) return;

    try {
      const newId = `${machine.id}_clone_${Date.now()}`;
      const newName = `${machine.name} (Clone)`;
      
      await machineService.cloneMachine(machine.id, newId, newName);
      toast({
        title: 'Machine Cloned',
        description: `Machine cloned successfully as "${newName}"`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to clone machine:', error);
      toast({
        title: 'Error',
        description: 'Failed to clone machine',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      case 'maintenance':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Effects
  useEffect(() => {
    loadMachine();
  }, [machineId]);

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      disconnectWebSocket();
    };
  }, [machineId]);

  // Send game button events via unified input stream
  const sendGameEvent = (evt: { buttonId: number; action: number; timestamp: Date }) => {
    const action: ButtonAction = evt.action === 1 ? ButtonAction.PRESSED : ButtonAction.RELEASED;
    const sent = inputStream.sendButtonEvent(evt.buttonId, action);
    if (!sent) {
      toast({
        title: 'WebSocket not ready',
        description: 'Button event not sent',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Send touch events via unified input stream
  // Matches SAS example pattern: state 1 = pressed, 0 = released
  const sendTouchEvent = (evt: { x: number; y: number; action: number; timestamp: Date }) => {
    const touchAction: TouchAction = evt.action === 1 ? 'press' : 'release';
    inputStream.sendTouchEvent(evt.x, evt.y, touchAction);
  };

  // Touch handlers for main video stream
  // Uses offsetX/offsetY (position relative to element) — same pattern as SAS example:
  //   (ptrEvnt.offsetX * 0x7fff) / ptrEvnt.currentTarget.clientWidth
  const handleVideoTouchStart = (e: React.PointerEvent<HTMLVideoElement>) => {
    if (!session) return;

    const target = e.currentTarget;
    const x = Math.floor((e.nativeEvent.offsetX * 0x7fff) / target.clientWidth);
    const y = Math.floor((e.nativeEvent.offsetY * 0x7fff) / target.clientHeight);

    setIsTouching(true);
    sendTouchEvent({ x, y, action: 1, timestamp: new Date() });
  };

  const handleVideoTouchEnd = (e: React.PointerEvent<HTMLVideoElement>) => {
    if (!session) return;

    const target = e.currentTarget;
    const x = Math.floor((e.nativeEvent.offsetX * 0x7fff) / target.clientWidth);
    const y = Math.floor((e.nativeEvent.offsetY * 0x7fff) / target.clientHeight);

    setIsTouching(false);
    sendTouchEvent({ x, y, action: 0, timestamp: new Date() });
  };

  // Input stream connection is managed automatically by useInputEventsStream hook
  // (connects when machineId + sessionId + accessToken are available, disconnects on unmount)

  // Auto-start video stream when machine data is loaded and session is active
  useEffect(() => {
    if (session && machine) {
      // Start stream after a short delay to allow the video element to be rendered
      const timer = setTimeout(() => {
        if (!videoStream.status.connected) {
          videoStream.startStream();
        }
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      // Stop stream when no session
      videoStream.stopStream();
    }
  }, [session, machine]); // Re-run when session or machine changes

  const getAvailableBalance = (cur: string) => {
    const bal = balancesRef.current.find((b: any) => b.currency === cur);
    if (!bal) return 0;
    const val = (bal.available?.units || 0) + (bal.available?.nanos || 0) / 1e9;
    return val;
  };

  // keep balances in ref for quick lookup without re-render cycles
  const balancesRef = useRef<any[]>([]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  if (!machine) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Machine not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <IconButton
              aria-label="Go back"
              icon={<FiArrowLeft />}
              variant="ghost"
              onClick={() => navigate('/machines')}
            />
            <VStack align="start" spacing={1}>
              <Heading size="lg">{machine.name || 'Unnamed Machine'}</Heading>
              <Text color="gray.500" fontSize="sm">ID: {machine.id}</Text>
            </VStack>
          </HStack>
          
          <HStack spacing={3}>
            <Tooltip label={isConnected ? 'Real-time updates active' : 'Real-time updates inactive'}>
              <Badge
                colorScheme={isConnected ? 'green' : 'red'}
                variant="solid"
                display="flex"
                alignItems="center"
                gap={2}
              >
                {isConnected ? <FiWifi /> : <FiWifiOff />}
                {isConnected ? 'Live' : 'Offline'}
              </Badge>
            </Tooltip>
            
            <Button
              leftIcon={<FiRefreshCw />}
              variant="outline"
              onClick={loadMachine}
              size="sm"
            >
              Refresh
            </Button>
          </HStack>
        </Flex>

        <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
          {/* Main Info */}
          <GridItem>
            <VStack spacing={6} align="stretch">
              {/* Status Card */}
              <Card>
                <CardHeader>
                  <Heading size="md">Current Status</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Flex justify="space-between" align="center">
                      <Badge
                        colorScheme={getStatusColor(machine.status)}
                        variant="solid"
                        fontSize="lg"
                        px={4}
                        py={2}
                      >
                        {machine.status?.toUpperCase() || 'UNKNOWN'}
                      </Badge>
                      
                      <HStack>
                                                 <Button
                           leftIcon={<FiPlay />}
                           colorScheme="green"
                           size="sm"
                           onClick={handleEnableMachine}
                           isDisabled={machine.status === 'FREE'}
                         >
                           Enable
                         </Button>
                         <Button
                           leftIcon={<FiPause />}
                           colorScheme="red"
                           size="sm"
                           onClick={handleDisableMachine}
                           isDisabled={machine.status === 'DISABLED'}
                         >
                           Disable
                         </Button>
                      </HStack>
                    </Flex>
                    
                    {lastUpdate && (
                      <Text fontSize="sm" color="gray.500">
                        Last updated: {lastUpdate.toLocaleString()}
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {/* Machine Details */}
              <Card>
                <CardHeader>
                  <Heading size="md">Machine Details</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text fontWeight="medium">Category:</Text>
                      <Text>{machine.category || 'Not specified'}</Text>
                    </HStack>
                    
                                         <HStack justify="space-between">
                       <Text fontWeight="medium">Machine ID:</Text>
                       <Text>{machine.id || 'Not specified'}</Text>
                     </HStack>
                    
                    <HStack justify="space-between">
                      <Text fontWeight="medium">Stream Available:</Text>
                      <Badge colorScheme={machine.stream_url ? 'green' : 'gray'}>
                        {machine.stream_url ? 'Yes' : 'No'}
                      </Badge>
                    </HStack>
                    
                                         {machine.last_seen && (
                       <HStack justify="space-between">
                         <Text fontWeight="medium">Last Seen:</Text>
                         <Text>{new Date(machine.last_seen).toLocaleDateString()}</Text>
                       </HStack>
                     )}
                  </VStack>
                </CardBody>
              </Card>

            </VStack>
          </GridItem>

          {/* Status History Sidebar */}
          <GridItem>
            <Card h="fit-content">
              <CardHeader>
                <HStack>
                  <FiClock />
                  <Heading size="md">Status History</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  {statusHistory.length === 0 ? (
                    <Text color="gray.500" textAlign="center">
                      No status history available
                    </Text>
                  ) : (
                    <List spacing={3}>
                      {statusHistory.slice().reverse().map((item, index) => (
                        <ListItem key={index}>
                          <HStack justify="space-between" align="start">
                            <VStack align="start" spacing={1}>
                              <Badge
                                colorScheme={getStatusColor(item.status)}
                                size="sm"
                              >
                                {item.status}
                              </Badge>
                              <Text fontSize="xs" color="gray.500">
                                {item.timestamp.toLocaleTimeString()}
                              </Text>
                            </VStack>
                            {item.duration && (
                              <Text fontSize="xs" color="gray.400">
                                {item.duration}
                              </Text>
                            )}
                          </HStack>
                          {index < statusHistory.length - 1 && <Divider mt={3} />}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* Session Control */}
          <Card>
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md">Game Session</Heading>
                <Text fontSize="xs" color="gray.500">
                  Machine: {machine.status || 'unknown'} | User: {profile?.id ? '✓' : '✗'}
                </Text>
              </Flex>
            </CardHeader>
            <CardBody>
              {session ? (
                <VStack spacing={4}>
                  <Alert status="success">
                    <AlertIcon />
                    Active session in progress
                  </Alert>
                  
                  <SimpleGrid columns={2} spacing={4} w="full">
                    <Stat>
                      <StatLabel>Session ID</StatLabel>
                      <StatNumber fontSize="sm">{session.id}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Started</StatLabel>
                      <StatNumber fontSize="sm">
                        {session.started_at ? new Date(session.started_at).toLocaleString() : 'Unknown'}
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>

                  <VStack spacing={3} w="full">
                    {/* Quick Game Actions */}
                    <HStack w="full" spacing={2}>
                      <Button
                        leftIcon={<FiVideo />}
                        colorScheme="purple"
                        size="lg"
                        onClick={onGameOpen}
                        flex={1}
                      >
                        Open Game
                      </Button>
                      <Button
                        leftIcon={<FiPlay />}
                        colorScheme="green"
                        size="lg"
                        onClick={() => {
                          // Quick Spin - simulate button press
                          toast({
                            title: 'Quick Spin!',
                            description: 'Game spin initiated',
                            status: 'success',
                            duration: 2000,
                            isClosable: true,
                          });
                        }}
                        flex={1}
                      >
                        Quick Spin
                      </Button>
                      <Button
                        leftIcon={<FiSettings />}
                        colorScheme="orange"
                        size="lg"
                        onClick={() => {
                          // Auto Play
                          toast({
                            title: 'Auto Play',
                            description: 'Auto play mode activated',
                            status: 'info',
                            duration: 2000,
                            isClosable: true,
                          });
                        }}
                        flex={1}
                      >
                        Auto Play
                      </Button>
                    </HStack>
                    
                    {/* Balance Actions */}
                    <VStack w="full" spacing={2}>
                      <Text fontSize="sm" fontWeight="bold" alignSelf="start">Balance Management:</Text>
                      <HStack w="full" spacing={2}>
                        <Button
                          leftIcon={<FiDollarSign />}
                          colorScheme="green"
                          onClick={onTopUpOpen}
                          flex={1}
                          size="lg"
                          isDisabled={!session}
                        >
                          Пополнить
                        </Button>
                        <Button
                          leftIcon={<FiCreditCard />}
                          colorScheme="blue"
                          onClick={onCashOutOpen}
                          flex={1}
                          size="lg"
                        >
                          Вывести
                        </Button>
                      </HStack>
                      
                      <Text fontSize="sm" fontWeight="bold" alignSelf="start" mt={2}>Session Control:</Text>
                      <Button
                        leftIcon={<FiPause />}
                        colorScheme="red"
                        onClick={handleEndSession}
                        isLoading={sessionLoading}
                        w="full"
                        size="lg"
                      >
                        Завершить игровую сессию
                      </Button>
                    </VStack>
                  </VStack>
                </VStack>
              ) : (
                <VStack spacing={4}>
                  <Alert status="info">
                    <AlertIcon />
                    No active session. Start a new game session to play.
                  </Alert>
                  
                  {/* Debug Info */}
                  <Box p={3} bg="gray.50" borderRadius="md" w="full">
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Debug Info:</Text>
                    <Text fontSize="xs">Machine ID: {machine.id || 'Not set'}</Text>
                    <Text fontSize="xs">Machine Status: {machine.status || 'Not set'}</Text>
                    <Text fontSize="xs">User Profile ID: {profile?.id || 'Not set'}</Text>
                    <Text fontSize="xs">User: {profile?.display_name || profile?.id || 'Not set'}</Text>
                    <Text fontSize="xs">Session Loading: {sessionLoading ? 'Yes' : 'No'}</Text>
                  </Box>
                  
                  <VStack spacing={3} w="full">
                    {machine.status && machine.status !== 'active' && (
                      <Alert status="warning" size="sm">
                        <AlertIcon />
                        Machine status: {machine.status}. You can still start a session.
                      </Alert>
                    )}
                    
                    <Button
                      leftIcon={<FiPlay />}
                      colorScheme="green"
                      size="lg"
                      onClick={handleStartSession}
                      isLoading={sessionLoading}
                      isDisabled={machine.status === 'maintenance'}
                      w="full"
                    >
                      {sessionLoading 
                        ? 'Starting Session...' 
                        : machine.status === 'maintenance' 
                          ? 'Machine Under Maintenance' 
                          : 'Start Game Session'
                      }
                    </Button>
                    
                    {/* Test Button */}
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => {
                        console.log('Current machine:', machine);
                        console.log('Current user:', profile);
                        console.log('Machine service:', machineService);
                        
                        toast({
                          title: 'Debug Info',
                          description: `Machine: ${machine.id}, User: ${profile?.id}`,
                          status: 'info',
                          duration: 3000,
                          isClosable: true,
                        });
                      }}
                      w="full"
                    >
                      Test Debug Info
                    </Button>
                  </VStack>
                </VStack>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Live Stream - Near Game Controls for Convenience */}
        {session && (
          <Card>
              <CardHeader>
                  <Flex justify="space-between" align="center">
                      <HStack spacing={3}>
                          <Heading size="md">Live Stream</Heading>
                          {isSecureStream && (
                              <Tooltip label="Secure WebSocket connection (WSS)">
                                  <Badge colorScheme="green" variant="subtle">
                                      🔒 Secure
                                  </Badge>
                              </Tooltip>
                          )}
                      </HStack>
                      <HStack spacing={2} fontSize="sm" color="gray.500">
                          <Text>Status: {videoStream.status.connected ? 'Connected' : 'Disconnected'}</Text>
                          {videoStream.status.reconnectAttempts > 0 && (
                              <Text color="orange.500">
                                  Reconnecting... ({videoStream.status.reconnectAttempts}/5)
                              </Text>
                          )}
                          {videoStream.status.error && (
                              <Text color="red.500" fontSize="xs">
                                  Error: {videoStream.status.error}
                              </Text>
                          )}
                      </HStack>
                  </Flex>
              </CardHeader>

              // Stream URL display with security indicator
              <HStack justify="space-between" w="100%" fontSize="sm">
                  <HStack spacing={2}>
                      {isSecureStream && <Text color="green.500">🔒</Text>}
                      <Text color="gray.600">
                          Stream: {streamUrl}
                      </Text>
                  </HStack>
                  <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => {
                          navigator.clipboard.writeText(streamUrl);
                          toast({
                              title: 'Copied!',
                              description: 'Stream URL copied to clipboard',
                              status: 'success',
                              duration: 2000,
                              isClosable: true,
                          });
                      }}
                  >
                      Copy URL
                  </Button>
              </HStack>
            <CardBody>
              <VStack spacing={4}>
                {/* Main Video Stream with Touchscreen */}
                <Box
                  w="100%"
                  bg="black"
                  borderRadius="md"
                  overflow="hidden"
                  position="relative"
                  border="2px solid"
                  borderColor={isTouching ? "cyan.400" : "transparent"}
                  transition="border-color 0.2s"
                  boxShadow={isTouching ? "0 0 20px rgba(0, 255, 255, 0.3)" : "none"}
                >
                  <AspectRatio ratio={16/9}>
                    <video
                      id="mainVideoStream"
                      autoPlay
                      muted
                      playsInline
                      onPointerDown={handleVideoTouchStart}
                      onPointerUp={handleVideoTouchEnd}
                      onPointerLeave={(e) => {
                        if (isTouching) {
                          handleVideoTouchEnd(e);
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        background: '#000',
                        cursor: session ? 'crosshair' : 'default',
                        touchAction: 'none', // Prevent browser touch gestures
                      }}
                      onLoadStart={() => console.log('Video loading started')}
                      onCanPlay={() => console.log('Video can play')}
                      onError={(e) => console.error('Video error:', e)}
                    />
                  </AspectRatio>

                  {/* Touch indicator */}
                  {isTouching && (
                    <Box
                      position="absolute"
                      top={2}
                      right={2}
                      bg="cyan.500"
                      color="white"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                      zIndex={10}
                    >
                      👆 TOUCH
                    </Box>
                  )}

                  {/* Stream Status Overlay */}
                  <Box
                    position="absolute"
                    top={2}
                    left={2}
                    bg="blackAlpha.700"
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <HStack spacing={2}>
                      <Box
                        w={2}
                        h={2}
                        bg={videoStream.status.connected ? "red.500" : "gray.500"}
                        borderRadius="full"
                        animation={videoStream.status.connected ? "pulse 2s infinite" : "none"}
                      />
                      <Text>{videoStream.status.connected ? 'LIVE' : 'DISCONNECTED'}</Text>
                    </HStack>
                  </Box>
                  
                  {/* Stream Controls Overlay */}
                  <Box
                    position="absolute"
                    bottom={2}
                    right={2}
                  >
                    <HStack spacing={2}>
                      <IconButton
                        aria-label={videoStream.status.connected ? "Stop stream" : "Start stream"}
                        icon={videoStream.status.connected ? <FiPause /> : <FiPlay />}
                        size="sm"
                        colorScheme={videoStream.status.connected ? "red" : "green"}
                        onClick={() => {
                          if (videoStream.status.connected) {
                            videoStream.stopStream();
                          } else {
                            videoStream.startStream();
                          }
                        }}
                      />
                      <IconButton
                        aria-label="Reconnect stream"
                        icon={<FiRefreshCw />}
                        size="sm"
                        colorScheme="blue"
                        onClick={() => videoStream.reconnect()}
                        isDisabled={videoStream.status.connected}
                      />
                      <IconButton
                        aria-label="Toggle fullscreen"
                        icon={<FiVideo />}
                        size="sm"
                        colorScheme="whiteAlpha"
                        onClick={() => {
                          const video = document.getElementById('mainVideoStream') as HTMLVideoElement;
                          if (video) {
                            if (document.fullscreenElement) {
                              document.exitFullscreen();
                            } else {
                              video.requestFullscreen();
                            }
                          }
                        }}
                      />
                    </HStack>
                  </Box>
                </Box>
                
                {/* Stream Endpoints and Copy URL */}
                <HStack justify="space-between" w="100%" fontSize="sm">
                  <Text color="gray.600">
                    Main: {getStreamUrl('/stream/main')}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(getStreamUrl('/stream/main'));
                      toast({
                        title: 'Copied!',
                        description: 'Main stream URL copied to clipboard',
                        status: 'success',
                        duration: 2000,
                        isClosable: true,
                      });
                    }}
                  >
                    Copy URL
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Game Interface */}
        {session && (
          <GameInterface
            machineId={machine.id!}
            sessionId={session?.id || ''}
            isActive={!!session}
            onSendEvent={sendGameEvent}
            onSendTouchEvent={sendTouchEvent}
            streamBaseUrl={machine.stream_url}
          />
        )}
      </VStack>

      {/* Top Up Modal */}
      <Modal isOpen={isTopUpOpen} onClose={onTopUpClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Пополнить машину</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Сумма (Available: {getAvailableBalance(currency).toFixed(2)} {currency})</FormLabel>
                <NumberInput
                  value={topUpAmount}
                  onChange={(_, value) => setTopUpAmount(value || 0)}
                  min={1}
                  max={1000}
                  step={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl>
                <FormLabel>Валюта</FormLabel>
                <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {availableCurrencies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTopUpClose}>
              Отмена
            </Button>
            <Button colorScheme="green" onClick={handleTopUp} w="full" 
              isDisabled={!session || topUpAmount<=0 || (currency && topUpAmount>getAvailableBalance(currency))}>
              Пополнить
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Cash Out Modal */}
      <Modal isOpen={isCashOutOpen} onClose={onCashOutClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Вывести с машины</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Сумма</FormLabel>
                <NumberInput
                  value={cashOutAmount}
                  onChange={(_, value) => setCashOutAmount(value || 0)}
                  min={0}
                  max={1000}
                  step={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl>
                <FormLabel>Валюта</FormLabel>
                <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {availableCurrencies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCashOutClose}>
              Отмена
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleCashOut}
              isDisabled={cashOutAmount <= 0}
            >
              Вывести
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Game Modal */}
      {machine && (
        <GameModal
          isOpen={isGameOpen}
          onClose={onGameClose}
          machine={machine}
          session={session}
        />
      )}

      {/* Edit Machine Modal */}
      {machine && (
        <EditMachineModal
          isOpen={isEditOpen}
          onClose={onEditClose}
          machine={machine}
          onMachineUpdated={loadMachine}
        />
      )}
    </Container>
  );
};
